import React, { useState } from 'react';
import { Card, Button } from 'components/ui';
import {
    DocumentTextIcon,
    PlusIcon,
    ArrowDownTrayIcon,
    EyeIcon,
    EnvelopeIcon,
} from '@heroicons/react/24/outline';
import GenerateOfferLetterModal from './GenerateOfferLetterModal';
import { toast } from 'sonner';
import axiosInstance from 'utils/axios';
import EmailMessageModal from '../LeadActions/EmailMessageModal';
import { formatDisplayDate } from "../../../utils/dateUtils";

/**
 * OfferLetterTab Component
 * Dedicated tab for displaying and managing offer letters
 * Shown as a separate menu item in Lead Details tabs
 */
const OfferLetterTab = ({
    leadId,
    leadData,
    onOfferLetterGenerated = () => { },
    isMobile = false,
    progressData,
}) => {
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [downloading, setDownloading] = useState(false);
    const [viewing, setViewing] = useState(false);

    // Email modal states
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailAttachments, setEmailAttachments] = useState([]);
    const [preparingEmail, setPreparingEmail] = useState(false);

    const offerLetter = leadData?.offerLetter || null;
    // Only treat an offer letter as "generated" if it has an actual stored file (FilePath/FileName)
    // or a valid record Id. A stub object returned without a file should not show "Offer Letter Generated".
    const hasOfferLetter = !!(offerLetter && (offerLetter.FilePath || offerLetter.FileName || offerLetter.Id));

    // specific check for valid email to disable button if missing
    const leadEmail = leadData?.email || leadData?.Email || leadData?.EmailAddress;
    const hasValidEmail = leadEmail && leadEmail !== "N/A" && leadEmail.trim() !== "";

    const handleDownload = async () => {
        if (!offerLetter?.FilePath) {
            toast.error('Unable to download offer letter');
            return;
        }

        setDownloading(true);
        try {
            // Use dedicated DownloadOfferLetter endpoint with file path
            const response = await axiosInstance.get(
                `/api/Leads/DownloadOfferLetter`,
                {
                    params: { filePath: offerLetter.FilePath },
                    responseType: 'blob',
                }
            );

            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', offerLetter.FileName || 'OfferLetter.html');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success('Offer letter downloaded successfully');
        } catch (error) {
            console.error('Error downloading offer letter:', error);
            toast.error('Failed to download offer letter');
        } finally {
            setDownloading(false);
        }
    };

    const handleView = async () => {
        if (!offerLetter?.FilePath) {
            toast.error('Unable to view offer letter');
            return;
        }

        setViewing(true);
        try {
            const response = await axiosInstance.get(
                `/api/Leads/DownloadOfferLetter`,
                {
                    params: { filePath: offerLetter.FilePath },
                    responseType: 'blob'
                }
            );

            const contentType = response.headers['content-type'] || 'application/pdf';
            const file = new Blob([response.data], { type: contentType });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL, '_blank');
        } catch (error) {
            console.error('View error:', error);
            toast.error('Failed to view offer letter');
        } finally {
            setViewing(false);
        }
    };

    const handleSendEmail = async () => {
        if (!hasValidEmail) {
            toast.error("Lead has no email address");
            return;
        }

        setPreparingEmail(true);
        try {
            // Fetch the offer letter blob
            const response = await axiosInstance.get(
                `/api/Leads/DownloadOfferLetter`,
                {
                    params: { filePath: offerLetter.FilePath },
                    responseType: 'blob'
                }
            );

            const contentType = response.headers['content-type'] || 'application/pdf';
            const extension = contentType.includes('html') ? 'html' : 'pdf';
            const fileName = `OfferLetter_${leadData?.name?.replace(/\s+/g, '_') || 'Candidate'}.${extension}`;

            // Create a File object
            const file = new File([response.data], fileName, {
                type: contentType
            });

            // Set attachments and open modal
            setEmailAttachments([file]);
            setShowEmailModal(true);
        } catch (error) {
            console.error('Error preparing email attachment:', error);
            toast.error('Failed to prepare offer letter for email');
        } finally {
            setPreparingEmail(false);
        }
    };

    const handleOfferLetterGenerated = () => {
        setShowGenerateModal(false);
        onOfferLetterGenerated();
    };

    return (
        <>
            <Card className="h-full border-none shadow-none p-0">
                {/* Header Section */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h4 className="text-lg font-bold text-gray-800 dark:text-white mb-1">
                            Offer Letter
                        </h4>
                        <p className="text-gray-500 text-sm">
                            Manage and track candidate offer details
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {hasOfferLetter && (
                            <Button
                                size="sm"
                                variant="outlined"
                                disabled={leadData?.IsStudent}
                                className={leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}
                                onClick={() => {
                                    setModalMode('edit');
                                    setShowGenerateModal(true);
                                }}
                            >
                                Edit Offer Letter
                            </Button>
                        )}                        
                    </div>
                </div>

                {hasOfferLetter ? (
                    <div className="space-y-6 animate-fade-in">
                        {/* Status Banner */}
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg p-4">
                            <div className="flex items-start">
                                <DocumentTextIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400 mt-1" />
                                <div className="ml-3 flex-1">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                        <div>
                                            <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                                                Offer Letter Generated
                                            </h3>
                                            <div className="mt-1 space-y-1">
                                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                                    <span className="font-medium">Date:</span>
                                                    {formatDisplayDate(offerLetter.CreatedAt)}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                                    <span className="font-medium">Candidate:</span> {leadData?.name || 'N/A'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                size="md"
                                                onClick={handleView}
                                                disabled={viewing}
                                                className="flex-1 sm:flex-none justify-center"
                                            >
                                                <EyeIcon className="h-5 w-5 mr-2" />
                                                {viewing ? 'Opening...' : 'View'}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                size="md"
                                                onClick={handleSendEmail}
                                                disabled={preparingEmail || !hasValidEmail || leadData?.IsStudent}
                                                title={leadData?.IsStudent ? "Option disabled for students" : (!hasValidEmail ? "Email address not available for this lead" : "Send offer letter via email")}
                                                className={`flex-1 sm:flex-none justify-center ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                                            >
                                                <EnvelopeIcon className="h-5 w-5 mr-2" />
                                                {preparingEmail ? 'Preparing...' : (!hasValidEmail ? 'No Email' : 'Email')}
                                            </Button>

                                            <Button
                                                variant="solid"
                                                color="primary"
                                                size="md"
                                                onClick={handleDownload}
                                                disabled={downloading}
                                                className="flex-1 sm:flex-none justify-center"
                                            >
                                                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                                                {downloading ? 'Downloading...' : 'Download'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="bg-gray-50 dark:bg-dark-800 rounded-lg p-4">
                            <h5 className="text-sm font-medium text-gray-900 dark:text-dark-100 mb-2">
                                About this offer letter
                            </h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                This offer letter is ready. You can view it, download it to your device, or email it directly to the candidate using the buttons above.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-16 bg-gray-50 dark:bg-dark-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-dark-600">
                        <div className="flex justify-center mb-4">
                            <div className="p-4 bg-gray-200 dark:bg-dark-700 rounded-full">
                                <DocumentTextIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                            </div>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                            No Offer Letter Yet
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                            Generate an offer letter for this candidate by clicking the button below.
                            You&apos;ll need to provide details and click on the generate button.
                        </p>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="md"
                            disabled={leadData?.IsStudent}
                            className={leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}
                            onClick={() => {
                                setModalMode('create');
                                setShowGenerateModal(true);
                            }}
                        >
                            <PlusIcon className="mr-2 h-4 w-4" />
                            Get Started
                        </Button>
                    </div>
                )}

            </Card>


            {/* Generate Offer Letter Modal */}
            <GenerateOfferLetterModal
                isOpen={showGenerateModal}
                onClose={() => setShowGenerateModal(false)}
                leadId={leadId}
                leadData={leadData}
                progressData={progressData}
                onSuccess={handleOfferLetterGenerated}
                mode={modalMode}
            />

            {/* Email Modal */}
            <EmailMessageModal
                isOpen={showEmailModal}
                onClose={() => {
                    setShowEmailModal(false);
                    setEmailAttachments([]); // Clear attachments on close
                }}
                leadData={leadData}
                initialAttachments={emailAttachments}
            />
        </>
    );
};

export default OfferLetterTab;
